import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { GridSortModel } from '@mui/x-data-grid';

import { InviteItem, InviteListItem } from '@vyce/core/src/types';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { formatSortModel } from '@vyce/core/src/utils/sorting';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

import { InviteProps } from '../types';
import { InviteRequestFilters } from './useInviteFilters';

const defaultSortModel: GridSortModel = [{ field: 'created_at', sort: 'desc' }];

// A retry settles within seconds; look again a little later in case it was still running
const RETRY_REFRESH_DELAYS_MS = [4000, 10000];

// Stable default — a fresh {} per render would refetch forever
const NO_FILTERS: InviteRequestFilters = {};

export const useInvitesData = (
  {
    selectedCompanyId,
    isLegend = false,
    handleInviteButtonClick,
    deleteInviteRequest,
    getInvitesRequest,
    resendInviteRequest,
    retryInviteItemRequest,
    reloadKey,
    statuses,
    modules,
  }: InviteProps,
  filters: InviteRequestFilters = NO_FILTERS
) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [invitedUsers, setInvitedUsers] = useState<InviteListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [retryingItemIds, setRetryingItemIds] = useState<Set<string>>(new Set());
  const refreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const {
    sortModel,
    offset,
    page,
    substring,
    total,
    setTotal,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({ defaultSortModel });

  // `silent` keeps the grid on screen — a background refresh after a retry shouldn't flash a loader
  const getInvites = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!isLegend && !selectedCompanyId) {
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      try {
        const defaultOptions = {
          limit: GRID_PAGE_SIZE,
          offset,
          substring,
          order_by: formatSortModel<InviteListItem>(sortModel),
          modules,
          ...filters,
          // A page that fixes the statuses (Pending invites) keeps them whatever the filter says
          statuses: statuses ?? filters.statuses,
        };
        const options = isLegend ? defaultOptions : { ...defaultOptions, companyId: selectedCompanyId };
        const res = await getInvitesRequest(options);
        setInvitedUsers(res.data.items);
        setTotal(res.data.count);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedCompanyId,
      substring,
      offset,
      sortModel,
      isLegend,
      statuses,
      modules,
      filters,
      getInvitesRequest,
      setTotal,
    ]
  );

  const resendInvite = async (inviteId: string, companyId: string) => {
    if (!companyId) {
      return;
    }
    try {
      await resendInviteRequest(companyId, inviteId);
      await getInvites();
      showNotification({ message: 'The Invitation has been resent', options: { variant: 'success' } });
    } catch (e) {
      handleServerError(e);
    }
  };

  const deleteInvite = async (inviteId: string, companyId: string) => {
    if (!companyId) {
      return;
    }
    try {
      const res = await deleteInviteRequest(companyId, inviteId);
      await getInvites();
      showNotification({ message: res.data.message, options: { variant: 'success' } });
    } catch (e) {
      handleServerError(e);
    }
  };

  const scheduleRefresh = useCallback(() => {
    refreshTimersRef.current.forEach(clearTimeout);
    refreshTimersRef.current = RETRY_REFRESH_DELAYS_MS.map(delay =>
      setTimeout(() => getInvites({ silent: true }), delay)
    );
  }, [getInvites]);

  const setItemRetrying = (itemId: string, retrying: boolean) =>
    setRetryingItemIds(ids => {
      const next = new Set(ids);
      retrying ? next.add(itemId) : next.delete(itemId);
      return next;
    });

  const retryInviteItem = async (invite: InviteListItem, item: InviteItem) => {
    if (!retryInviteItemRequest || !invite.company_id) {
      return;
    }
    setItemRetrying(item.uuid, true);
    try {
      const res = await retryInviteItemRequest(invite.company_id, invite.uuid, item.uuid);
      showNotification({
        message: res.data?.message || 'Retrying. The result will show on this invite in a moment.',
        options: { variant: 'success' },
      });
      // The server has put the line back to pending already — show "Retrying…" until it settles
      setInvitedUsers(rows =>
        rows.map(row =>
          row.uuid !== invite.uuid
            ? row
            : {
                ...row,
                items: (row.items ?? []).map(line =>
                  line.uuid === item.uuid
                    ? { ...line, status: 'pending', reason: 'manual_resend', failure_reason: null }
                    : line
                ),
              }
        )
      );
      scheduleRefresh();
    } catch (e: any) {
      handleServerError(e);
      // 409: someone else retried it, or it already worked — show what is true now.
      // 503: nothing changed, the button stays for another go.
      if (e?.response?.status === 409) {
        getInvites({ silent: true });
      }
    } finally {
      setItemRetrying(item.uuid, false);
    }
  };

  // New filters, new result set — start from its first page
  useEffect(() => {
    handlePageChange(0);
  }, [filters, handlePageChange]);

  useEffect(() => {
    getInvites();
  }, [getInvites, reloadKey]);

  useEffect(() => () => refreshTimersRef.current.forEach(clearTimeout), []);

  return {
    loading,
    invitedUsers,
    page,
    sortModel,
    total,
    handleInviteButtonClick,
    handleSortModelChange,
    handlePageChange,
    resendInvite,
    deleteInvite,
    retryInviteItem: retryInviteItemRequest ? retryInviteItem : undefined,
    retryingItemIds,
    handleSearchChange,
  };
};
